import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bell, Plus } from 'lucide-react';
import { updateProjectModal } from '../../app/features/project/projectSlice';
import { notifyProjectParticipants } from '../../apiServices/messageApi';
import { Avatar } from '@/components/ui/Avatar';
import { ConfirmDialog } from '@/components/ui/Modal';
import { useNotification } from '@/hooks/useNotification';

interface User {
  uid: string;
  given_name: string;
  family_name: string;
  img?: string;
}

export function UsersList() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const { notify } = useNotification();
  const { users, error, loading, modalOpen, projectId } = useSelector(
    ({ project }: { project: Record<string, unknown> }) => project,
  ) as {
    users: User[] | null;
    error: boolean;
    loading: boolean;
    modalOpen: boolean;
    projectId: string;
  };
  const dispatch = useDispatch();

  useEffect(() => {
    if (error) {
      notify({ id: String(Date.now()), type: 'error', message: 'Error fetching project participants' });
    }
  }, [error, notify]);

  const toggleModal = () => {
    if (!error) dispatch(updateProjectModal(!modalOpen));
  };

  const handleNotify = async () => {
    setNotifying(true);
    try {
      await notifyProjectParticipants(projectId);
      notify({ id: String(Date.now()), type: 'success', message: 'Reminder sent to project participants' });
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not send reminder' });
    } finally {
      setNotifying(false);
      setConfirmOpen(false);
    }
  };

  if (loading || !users) {
    return (
      <div className="flex items-center gap-1 opacity-50">
        {['A B', 'C D', 'E F'].map((n) => (
          <Avatar key={n} name={n} size="sm" />
        ))}
      </div>
    );
  }

  if (!users.length) return null;

  const displayUsers = users.slice(0, 5);
  const surplus = users.length > 5 ? users.length - 5 : 0;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={toggleModal}
        className="flex items-center gap-0.5 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-secondary"
        title="Manage participants"
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent">
          <Plus className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        {surplus > 0 && (
          <span className="ml-0.5 text-xs font-medium text-muted-foreground">+{surplus}</span>
        )}
        <div className="flex -space-x-1.5">
          {displayUsers.map((user) => (
            <Avatar
              key={user.uid}
              src={user.img}
              name={`${user.given_name} ${user.family_name}`}
              size="sm"
              className="ring-2 ring-white"
            />
          ))}
        </div>
      </button>

      <button
        onClick={() => setConfirmOpen(true)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-muted-foreground"
        title="Send reminder to participants"
      >
        <Bell className="h-4 w-4" />
      </button>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleNotify}
        title="Send reminder?"
        description="Send a notification to all project participants."
        confirmLabel="Send"
        loading={notifying}
      />
    </div>
  );
}

export default UsersList;
